<template>
  <div class="insurance-chart-block-wrapper">
    <div class="insurance-chart-block">
      <div class="insurance-chart-block__header">
        <p class="insurance-chart-block__column-title insurance-chart-block__column-title--left">Компанія</p>
        <p class="insurance-chart-block__column-title insurance-chart-block__column-title--right">Продано послуг</p>
      </div>
      <div class="insurance-chart-block__body">
        <div class="insurance-chart-block__list-container">
          <div class="insurance-chart-block__list-item" v-for="company in sortedCompanies" :key="company.id">
            <div class="insurance-chart-block__item-content insurance-chart-block__item-content--left">
              <InsuranceCompanyLogo :logo="company.logo" :alternativeText="company.name" />
              <div class="insurance-chart-block__item-details">
                <p class="insurance-chart-block__item-name">{{ company.name }}</p>
                <p class="insurance-chart-block__item-website">{{ company.website }}</p>
              </div>
            </div>
            <div class="insurance-chart-block__item-content insurance-chart-block__item-content--right">
              <div class="insurance-chart-block__progress-bar">
                <div class="insurance-chart-block__progress-fill" :style="{ width: getProgressBarWidth(company.value) }"></div>
              </div>
              <p class="insurance-chart-block__item-value">{{ formatNumberCompact(company.value, getAffix(type).prefix, getAffix(type).suffix) }}</p>
            </div>
          </div>
        </div>
        <Paginator
          v-if="!!sortedCompanies.length"
          class="insurance-chart-paginator"
          :hide-if-one-page="false"
          :current-page="currentPage"
          :page-size="insuranceCompaniesLimit"
          :items-count="getCompaniesCount"
          @page-changed="paginate"
        />
      </div>
      <slot></slot>
    </div>
  </div>
</template>

<script>
import { compactNumberFormatter } from '@/shared/lib/number';
import { InsuranceCompanyLogo } from '@/shared/ui/insurance-company-logo';
import { Paginator } from '@/shared/ui/paginator';

export default {
  props: {
    type: {
      type: String,
      required: true,
    },
    insuranceCompanies: {
      type: Array,
      default: () => [],
    },
    insuranceCompaniesLimit: {
      type: Number,
      default: 7,
    },
  },
  data() {
    return {
      currentPage: 1,
    };
  },
  components: { InsuranceCompanyLogo, Paginator },
  mixins: [ compactNumberFormatter ],
  computed: {
    getCompaniesCount() {
      return this.insuranceCompanies.length;
    },
    sortedCompanies() {
      const insuranceCompaniesCopy = [...this.insuranceCompanies];
      const sortedInsuranceCompanies = insuranceCompaniesCopy.sort((a, b) => b.value - a.value);
      const start = (this.currentPage - 1) * this.insuranceCompaniesLimit;
      const end = start + this.insuranceCompaniesLimit;

      return sortedInsuranceCompanies.slice(start, end);
    },
    maxValue() {
      if (this.insuranceCompanies.length === 0) {
        return 0;
      }
      return Math.max(...this.insuranceCompanies.map((partner) => partner.value));
    },
  },
  methods: {
    getProgressBarWidth(value) {
      if (this.maxValue === 0) {
        return '0%';
      }
      const percentage = (value / this.maxValue) * 100;
      return `${percentage}%`;
    },
    getAffix(type) {
      const affixes = {
        amount: { prefix: '₴', suffix: '' },
        earnings: { prefix: '₴', suffix: '' },
        count: { prefix: '', suffix: ' ШТ' },
      };

      return affixes[type] || { prefix: '', suffix: '' };
    },
    paginate(page) {
      this.currentPage = page;
      this.$emit('update-slider', 200);
    },
  },
};
</script>

<style lang="scss" scoped>
.insurance-chart-block-wrapper {
  max-width: 100%;
  margin: 0 auto 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 1px solid #E5E5E7;
  border-radius: 12px;
  background-color: #ffffff;
}

.insurance-chart-block {
  position: relative;
  width: 100%;
  min-height: 600px;
  padding-bottom: 70px;

  &__header {
    background-color: #FAFAFA;
    border-radius: 12px 12px 0 0;
    padding: 12px 24px;
    border-bottom: 1px solid #E5E5E7;
    font-size: 12px;
    font-weight: 500;
    display: flex;
    align-items: center;
  }

  &__column-title {
    &--left,
    &--right {
      width: 50%;
    }
  }

  &__list-item {
    border-bottom: 1px solid #E9EAEB;
    display: flex;
  }

  &__item-content {
    display: flex;
    align-items: center;

    &--left {
      padding: 14px 24px;
      width: 50%;
    }

    &--right {
      padding: 14px 24px 16px 0;
      width: 50%;
      justify-content: space-between;
    }
  }

  &__item-details {
    display: flex;
    flex-direction: column;
  }

  &__item-name {
    width: 100%;
    font-size: 14px;
    font-weight: 500;
  }

  &__item-website {
    width: 100%;
    font-size: 14px;
    font-weight: 400;
  }

  &__progress-bar {
    width: 100%;
    background-color: #F5F5F5;
    height: 8px;
    border-radius: 4px;
    margin-right: 12px;
    overflow: hidden;
  }

  &__progress-fill {
    height: 100%;
    background-color: #7F56D9;
    border-radius: 4px;
    transition: width 0.3s ease-out;
  }

  &__item-value {
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    width: 75px;
  }

  &__pagination-controls {
    margin: 12px 24px;
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__pagination-info {
    font-size: 14px;
  }
}

@media (max-width: 768px) {
  .insurance-chart-block {
    &__list-item {
      flex-direction: column;
    }

    &__item-content {
      &--left,
      &--right {
        width: 100%;
        padding: 8px 24px;
      }
      &--right {
        padding-top: 0;
        justify-content: flex-start;
      }
    }
  }
}

.insurance-chart-paginator {
  position: absolute;
  bottom: 5px;
  left: 0;
  right: 0;
}
</style>
